// <auto-generated>
// This file is automatically generated from DSL.
// Do not modify this file directly. Changes will be overwritten.
// Generated: 2026-06-16T14:14:19.245Z
// </auto-generated>

import { Fragment, useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  ListSubheader,
  Divider,
  Typography,
  Tooltip,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText as ListItemTextComponent,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowDropDown, RadioButtonUnchecked, Check, WarningAmberRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SimulationCompleteError } from '@/lib/apiClient';
import { useButtonFeedback } from '@/hooks/useButtonFeedback';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CommandResultSnackbar, type CommandResult } from '@/components/CommandResultSnackbar';
import { acknowledgementWith, getWarningsRequiringAcknowledgement, type RuleWarning } from '@/lib/ruleWarnings';
import { usePlanHeaderActions, WorkflowActions } from '@/finance/hooks/usePlanHeaderActions';
import { useVisibleMenuActionExtras, groupMenuActionExtras, MenuActionExtraItem } from '@/config/menuActionExtras';
import { applyClientChecklist } from '@/core/policies/requiredFieldsChecklist';

interface PlanHeaderActionsMenuProps {
  entityId: string;
  /**
   * C7: the detail view's current (possibly unsaved) form data. When given, each action's
   * required-field checklist is re-evaluated against it — conditional required fields included —
   * so requiredness shows BEFORE submit; without it the server verdict on the saved entity stands.
   */
  formData?: Record<string, unknown>;
  onCommandError?: (error: unknown) => void;
  onCommandSuccess?: () => void;
}

/**
 * Actions menu component for PlanHeader
 * Displays available commands and workflow actions grouped by workflow
 */
export function PlanHeaderActionsMenu({ entityId, formData, onCommandError, onCommandSuccess }: PlanHeaderActionsMenuProps) {
  const { t } = useTranslation();
  const { availableActions: serverActions, availableCommands: serverCommands, isLoading, triggerWorkflow, executeCommandMutation, isTriggering, isExecuting } = usePlanHeaderActions(entityId);
  const feedback = useButtonFeedback();

  // C7: re-derive the checklist (missing fields, enablement, reason) from the current form data.
  const missingLabel = (fields: string[]) => t('actions.missingRequired') + ': ' + fields.join(', ');
  const availableCommands = serverCommands.map((command) => applyClientChecklist(command, 'PlanHeader', formData, missingLabel));
  const availableActions = serverActions.map((workflow) => ({
    ...workflow,
    actions: workflow.actions.map((action) => applyClientChecklist(action, 'PlanHeader', formData, missingLabel)),
  }));

  // C8: a command refused because an advisory rule fired waits here for the user's
  // acknowledgement; the same command is then resent with the accepted rule keys.
  const [pendingAcknowledgement, setPendingAcknowledgement] = useState<
    { input: { commandName: string; input?: Record<string, any> }; warnings: RuleWarning[] } | null
  >(null);
  const [acknowledgedOutcome, setAcknowledgedOutcome] = useState<CommandResult | null>(null);

  // Wrap executeCommand to handle errors and success. With `acknowledged` the command is the
  // resubmission of a refused one: the accepted warnings ride along as their rule keys.
  const executeCommand = (input: { commandName: string; input?: Record<string, any> }, acknowledged?: RuleWarning[]) => {
    const acknowledgedKeys = acknowledged
      ? acknowledgementWith(input.input?.acknowledgedWarnings, acknowledged)
      : null;
    const payload = acknowledgedKeys
      ? { ...input, input: { ...(input.input ?? {}), acknowledgedWarnings: acknowledgedKeys } }
      : input;
    executeCommandMutation.mutate(payload, {
      onSuccess: () => {
        feedback.onSuccess();
        if (acknowledgedKeys) {
          setAcknowledgedOutcome({
            severity: 'warning',
            // Every key the command carried, not just the last round's: after an accumulating
            // resubmission those differ.
            message: t('actions.completedWithWarnings', {
              count: acknowledgedKeys.length,
              defaultValue: 'Completed with {{count}} acknowledged warning(s)',
            }),
          });
        }
        if (onCommandSuccess) {
          onCommandSuccess();
        }
      },
      onError: (error: unknown) => {
        if (error instanceof SimulationCompleteError) return;
        // Warn BEFORE anything goes through: the server refused without saving, so the user
        // decides here. A warning that appeared since the last acknowledgement asks again —
        // staging `payload` rather than `input` keeps the keys already accepted, so the next
        // round adds to them instead of trading one refusal for another.
        const warnings = getWarningsRequiringAcknowledgement(error);
        if (warnings && warnings.length > 0) {
          setPendingAcknowledgement({ input: payload, warnings });
          return;
        }
        feedback.onError();
        if (onCommandError) {
          onCommandError(error);
        }
      }
    });
  };

  const handleAcknowledge = () => {
    if (!pendingAcknowledgement) return;
    const { input, warnings } = pendingAcknowledgement;
    setPendingAcknowledgement(null);
    executeCommand(input, warnings);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Bespoke (non-DSL) menu actions registered for this entity, e.g. "Generate headers" on PlanHeader.
  // Permission-filtered, so the section caption and its divider are never stranded above nothing.
  const menuExtras = useVisibleMenuActionExtras('PlanHeader');
  const menuExtraGroups = groupMenuActionExtras(menuExtras);
  const [activeExtraId, setActiveExtraId] = useState<string | null>(null);
  const activeExtra = menuExtras.find((e) => e.id === activeExtraId) ?? null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWorkflowActionClick = (workflowName: string, stepName: string) => {
    triggerWorkflow(
      { workflowName, stepName },
      {
        onSuccess: () => feedback.onSuccess(),
        onError: () => feedback.onError(),
      }
    );
    handleClose();
  };

  const handleCommandClick = (commandName: string) => {
    executeCommand({
      commandName,
    });
    handleClose();
  };

  // Show button if we have actions/commands OR if still loading (to avoid flicker)
  // Always show if there are any commands/actions defined, even if disabled
  const hasActions = (availableActions && availableActions.length > 0) || (availableCommands && availableCommands.length > 0) || menuExtras.length > 0;

  // Debug: Log what we're checking
  if (process.env.NODE_ENV === 'development') {
    console.log('OrderActionsMenu render check:', {
      isLoading,
      hasActions,
      availableActionsCount: availableActions?.length || 0,
      availableCommandsCount: availableCommands?.length || 0,
      availableActions,
      availableCommands
    });
  }

  // Show button while loading OR if we have actions
  if (!isLoading && !hasActions) {
    return null;
  }

  return (
    <>
      <Button
        variant={feedback.isSuccess ? 'contained' : 'outlined'}
        onClick={handleClick}
        startIcon={feedback.isSuccess ? <Check /> : undefined}
        endIcon={!feedback.isSuccess ? <ArrowDropDown /> : undefined}
        disabled={isTriggering || isExecuting}
        color={feedback.isSuccess ? 'success' : feedback.isError ? 'error' : 'primary'}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          px: 2,
          transition: 'all 0.3s ease',
        }}
      >
        {feedback.isSuccess ? t('common.done') : feedback.isError ? t('common.failed') : t('actions.actions')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // Focus the list itself on open, not "the first child": the children are sections, and
        // MUI would hand a fragment the focus and lose the arrow keys with it. From the list,
        // ArrowDown reaches the first available row and Enter runs it.
        disableAutoFocusItem
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Commands section */}
        {availableCommands && availableCommands.length > 0 && (
          <>
            <ListSubheader
              disableSticky
              sx={{ px: 2, py: 1, lineHeight: 1.5, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', bgcolor: 'transparent' }}
            >
              {t('actions.commands')}
            </ListSubheader>
            {availableCommands.map((command) => {
              const hasMissingFields = command.missingFields && command.missingFields.length > 0;
              const tooltipTitle = command.enabled
                ? command.displayName
                : hasMissingFields
                ? (command.reason || t('actions.missingRequired')) + '\n\n' + t('actions.missingFields') + '\n' + command.missingFields?.map(f => '• ' + f).join('\n')
                : command.reason || t('actions.commandNotAvailable');

              return (
                <Tooltip
                  key={command.id}
                  title={tooltipTitle}
                  placement="left"
                  arrow
                  // The reason DESCRIBES the row; it must never become its name. A Tooltip labels
                  // its child by default (aria-label={title}), and since the <span> wrapper was
                  // dropped that child is the MenuItem itself — so an unavailable command announced
                  // "Missing required fields: ..." and never said which command it was.
                  describeChild
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: hasMissingFields ? 400 : 'none',
                        whiteSpace: hasMissingFields ? 'pre-line' : 'normal',
                      },
                    },
                  }}
                >
                  <MenuItem
                      onClick={command.enabled && !isExecuting ? () => handleCommandClick(command.name) : undefined}
                      disabled={isExecuting}
                      aria-disabled={!command.enabled || isExecuting}
                      disableRipple={!command.enabled}
                      sx={{
                        pl: 3,
                        // Unavailable: inert rather than MUI-disabled, so its tooltip (the reason) still
                        // opens on hover and focus, while the arrow keys skip it like any disabled row.
                        ...(!command.enabled && { cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }),
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: hasMissingFields ? 1.5 : 1,
                        // Every row carries the accent gutter, so nothing shifts when one lights up.
                        borderLeft: '3px solid transparent',
                        // A blocked-by-fields action has to read as unavailable AND say why. MUI dims
                        // the whole disabled item to 38%, which buried the checklist that is the entire
                        // point — so the dimming moves onto the label alone and the reason keeps full
                        // contrast, on a warning-tinted row the eye finds in a list of greyed ones.
                        ...(hasMissingFields && {
                          borderLeftColor: 'warning.main',
                          backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.09),
                        }),
                        // MUI disables a non-button row with pointer-events: none, which also
                        // swallows the hover its Tooltip needs — so while a command runs, the reason
                        // and the missing-field checklist could not be read on any row, exactly when
                        // the user goes looking for them. Keep every row hoverable (the click is
                        // refused above) and keep the dimming off a blocked-by-fields row. One merged
                        // block: a second '&.Mui-disabled' key would replace this one wholesale.
                        '&.Mui-disabled': {
                          pointerEvents: 'auto',
                          ...(hasMissingFields && { opacity: 1 }),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {hasMissingFields && (
                          <WarningAmberRounded sx={{ fontSize: 18, color: 'warning.main', mr: 1 }} />
                        )}
                        <ListItemText
                          primary={command.displayName}
                          primaryTypographyProps={{
                            sx: {
                              fontWeight: !command.enabled ? 500 : 'normal',
                              // Still plainly disabled: the label is the part that greys out.
                              color: !command.enabled ? 'text.disabled' : 'text.primary',
                            },
                          }}
                        />
                        {hasMissingFields && (
                          <Chip
                            label={t('actions.nMissing', { count: command.missingFields?.length ?? 0 })}
                            size="small"
                            color="warning"
                            sx={{ ml: 1, height: 22, fontSize: '0.72rem', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      {hasMissingFields && (
                        <Box sx={{ mt: 0.75, ml: 3.5, width: '100%' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'warning.main',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              mb: 0.25,
                              display: 'block',
                            }}
                          >
                            {t('actions.missingFields')}
                          </Typography>
                          <List dense sx={{ py: 0, pl: 0 }}>
                            {command.missingFields?.map((field, index) => (
                              <ListItem key={index} sx={{ py: 0, pl: 0, minHeight: 22 }}>
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  <RadioButtonUnchecked
                                    sx={{
                                      fontSize: 14,
                                      color: 'warning.main',
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemTextComponent
                                  primary={field}
                                  primaryTypographyProps={{
                                    variant: 'caption',
                                    sx: {
                                      // The field names are the actionable information, so they keep
                                      // full contrast even though the row itself is disabled.
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      color: 'text.primary',
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </MenuItem>
                </Tooltip>
              );
            })}
            {availableActions && availableActions.length > 0 && <Divider />}
          </>
        )}
        {/* Workflow actions section */}
        {availableActions.map((workflow: WorkflowActions, workflowIndex: number) => (
          <Fragment key={workflow.workflowName}>
            {workflowIndex > 0 && <Divider />}
            <ListSubheader
              disableSticky
              sx={{ px: 2, py: 1, lineHeight: 1.5, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', bgcolor: 'transparent' }}
            >
              {workflow.workflowName}
            </ListSubheader>
            {workflow.actions.map((action) => {
              const hasMissingFields = action.missingFields && action.missingFields.length > 0;
              const tooltipTitle = action.enabled
                ? action.name
                : hasMissingFields
                ? (action.reason || t('actions.missingRequired')) + '\n\n' + t('actions.missingFields') + '\n' + action.missingFields?.map(f => '• ' + f).join('\n')
                : action.reason || t('actions.actionNotAvailable');

              return (
                <Tooltip
                  key={action.id}
                  title={tooltipTitle}
                  placement="left"
                  arrow
                  // The reason DESCRIBES the row; it must never become its name. A Tooltip labels
                  // its child by default (aria-label={title}), and since the <span> wrapper was
                  // dropped that child is the MenuItem itself — so an unavailable command announced
                  // "Missing required fields: ..." and never said which command it was.
                  describeChild
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: hasMissingFields ? 400 : 'none',
                        whiteSpace: hasMissingFields ? 'pre-line' : 'normal',
                      },
                    },
                  }}
                >
                  <MenuItem
                      onClick={action.enabled && !isTriggering ? () => handleWorkflowActionClick(action.workflowName, action.stepName) : undefined}
                      disabled={isTriggering}
                      aria-disabled={!action.enabled || isTriggering}
                      disableRipple={!action.enabled}
                      sx={{
                        pl: 3,
                        // Unavailable: inert rather than MUI-disabled, so its tooltip (the reason) still
                        // opens on hover and focus, while the arrow keys skip it like any disabled row.
                        ...(!action.enabled && { cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }),
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        py: hasMissingFields ? 1.5 : 1,
                        // Every row carries the accent gutter, so nothing shifts when one lights up.
                        borderLeft: '3px solid transparent',
                        // A blocked-by-fields action has to read as unavailable AND say why. MUI dims
                        // the whole disabled item to 38%, which buried the checklist that is the entire
                        // point — so the dimming moves onto the label alone and the reason keeps full
                        // contrast, on a warning-tinted row the eye finds in a list of greyed ones.
                        ...(hasMissingFields && {
                          borderLeftColor: 'warning.main',
                          backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.09),
                        }),
                        // MUI disables a non-button row with pointer-events: none, which also
                        // swallows the hover its Tooltip needs — so while a command runs, the reason
                        // and the missing-field checklist could not be read on any row, exactly when
                        // the user goes looking for them. Keep every row hoverable (the click is
                        // refused above) and keep the dimming off a blocked-by-fields row. One merged
                        // block: a second '&.Mui-disabled' key would replace this one wholesale.
                        '&.Mui-disabled': {
                          pointerEvents: 'auto',
                          ...(hasMissingFields && { opacity: 1 }),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        {hasMissingFields && (
                          <WarningAmberRounded sx={{ fontSize: 18, color: 'warning.main', mr: 1 }} />
                        )}
                        <ListItemText
                          primary={action.name}
                          primaryTypographyProps={{
                            sx: {
                              fontWeight: !action.enabled ? 500 : 'normal',
                              // Still plainly disabled: the label is the part that greys out.
                              color: !action.enabled ? 'text.disabled' : 'text.primary',
                            },
                          }}
                        />
                        {hasMissingFields && (
                          <Chip
                            label={t('actions.nMissing', { count: action.missingFields?.length ?? 0 })}
                            size="small"
                            color="warning"
                            sx={{ ml: 1, height: 22, fontSize: '0.72rem', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      {hasMissingFields && (
                        <Box sx={{ mt: 0.75, ml: 3.5, width: '100%' }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'warning.main',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              mb: 0.25,
                              display: 'block',
                            }}
                          >
                            {t('actions.missingFields')}
                          </Typography>
                          <List dense sx={{ py: 0, pl: 0 }}>
                            {action.missingFields?.map((field, index) => (
                              <ListItem key={index} sx={{ py: 0, pl: 0, minHeight: 22 }}>
                                <ListItemIcon sx={{ minWidth: 20 }}>
                                  <RadioButtonUnchecked
                                    sx={{
                                      fontSize: 14,
                                      color: 'warning.main',
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemTextComponent
                                  primary={field}
                                  primaryTypographyProps={{
                                    variant: 'caption',
                                    sx: {
                                      // The field names are the actionable information, so they keep
                                      // full contrast even though the row itself is disabled.
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      color: 'text.primary',
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </MenuItem>
                </Tooltip>
              );
            })}
          </Fragment>
        ))}

        {/* Bespoke menu actions registered for this entity (e.g. "Generate headers" on a PlanHeader),
            shown at the bottom, one captioned group per section. Empty for entities with none
            registered. A divider separates each group from whatever is above it. */}
        {menuExtraGroups.map((group, groupIndex) => (
          <Fragment key={group.section ?? groupIndex}>
            {(groupIndex > 0 || (availableCommands && availableCommands.length > 0) || (availableActions && availableActions.length > 0)) && <Divider />}
            {group.section && (
              <ListSubheader
                disableSticky
                sx={{ px: 2, py: 1, lineHeight: 1.5, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', bgcolor: 'transparent' }}
              >
                {group.section}
              </ListSubheader>
            )}
            {group.items.map((extra) => (
              <MenuActionExtraItem
                key={extra.id}
                extra={extra}
                onActivate={() => { setActiveExtraId(extra.id); handleClose(); }}
              />
            ))}
          </Fragment>
        ))}
      </Menu>
      {activeExtra && (
        <activeExtra.Dialog entityId={entityId} open onClose={() => setActiveExtraId(null)} />
      )}

      {/* C8: the app's one confirmation surface, asking before the command goes through */}
      <ConfirmDialog
        open={pendingAcknowledgement !== null}
        title={t('actions.warningsTitle', { defaultValue: 'Continue despite warnings?' })}
        message={t('actions.warningsMessage', { defaultValue: 'The action can still be completed. Continuing records that you accepted these warnings.' })}
        confirmLabel={t('actions.continueAnyway', { defaultValue: 'Continue anyway' })}
        cancelLabel={t('common.cancel', { defaultValue: 'Cancel' })}
        confirmDisabled={isExecuting}
        onConfirm={handleAcknowledge}
        onCancel={() => setPendingAcknowledgement(null)}
      >
        <List dense disablePadding sx={{ mb: 1 }}>
          {(pendingAcknowledgement?.warnings ?? []).map((warning) => (
            <ListItem key={warning.ruleKey} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <WarningAmberRounded fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemTextComponent primary={warning.message} secondary={warning.ruleKey} />
            </ListItem>
          ))}
        </List>
      </ConfirmDialog>

      {/* C8: the outcome of an acknowledged command — the app's one action-feedback surface */}
      <CommandResultSnackbar result={acknowledgedOutcome} onClose={() => setAcknowledgedOutcome(null)} />
    </>
  );
}
